import React, { useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useDebouncedValue } from "../hooks/useDebouncedValue"

export type Option = { id: number; name: string }

type Props = {
  queryKey: string
  // Server-side search — accepts the free-text term, returns rows.
  fetcher: (q: string) => Promise<unknown>
  value: number | null
  onChange: (id: number | null, name: string | null) => void
  // Name to show for `value`. Pass it whenever `value` can outlive this
  // component — a filter popover or modal body that unmounts while closed keeps
  // its id in the parent but takes the remembered name down with it, and a value
  // with no name to show renders neither the selection nor a working search.
  selectedName?: string | null
  placeholder?: string
  // DOM id for the search input, so a sibling <label htmlFor> can target it.
  inputId?: string
  // Ids to hide from results (e.g. already-assigned entities).
  excludeIds?: number[]
  // Map the fetcher payload to options. Defaults to a JSON:API shape
  // ({ data: [{ id, attributes: { fullName } }] }); override for other shapes.
  mapOptions?: (data: unknown) => Option[]
}

const defaultMapOptions = (data: unknown): Option[] => {
  const rows = (data as { data?: { id: string; attributes: { fullName: string } }[] })?.data ?? []
  return rows.map((row) => ({ id: Number(row.id), name: row.attributes.fullName }))
}

// Type-ahead picker backed by a server-side search endpoint. Scales selection to
// large orgs instead of rendering every option in a native <select>.
export const SearchableSelect: React.FC<Props> = ({
  queryKey,
  fetcher,
  value,
  onChange,
  selectedName,
  placeholder = "Search…",
  inputId,
  excludeIds = [],
  mapOptions = defaultMapOptions,
}) => {
  const [input, setInput] = useState("")
  const [pickedName, setPickedName] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debounced = useDebouncedValue(input)

  // Cancel a pending blur-close if the component unmounts inside the 150ms
  // window, so it doesn't setOpen on an unmounted component.
  useEffect(() => () => {
    if (blurTimer.current) clearTimeout(blurTimer.current)
  }, [])

  const { data, isFetching, isError } = useQuery(
    [queryKey, debounced],
    () => fetcher(debounced),
    { enabled: open, keepPreviousData: true }
  )

  const options: Option[] = mapOptions(data).filter((opt) => !excludeIds.includes(opt.id))

  // A caller-supplied name wins: it is the one that survives this component
  // unmounting. Without a name there is nothing to render as the selection, so
  // treat it as unselected and keep searching available — otherwise the input
  // stays visible with its results menu suppressed, which reads as a search box
  // that does nothing.
  const displayName = selectedName ?? pickedName
  const hasSelection = value != null && !!displayName

  // With keepPreviousData, `options` still holds the previous term's rows while a
  // new query is in flight. Only surface (and allow selecting) results once the
  // fetch settles, so a user can't pick a stale row under the "Searching…" state.
  const showOptions = !isFetching && !isError

  const select = (opt: Option) => {
    onChange(opt.id, opt.name)
    setPickedName(opt.name)
    setInput("")
    setOpen(false)
  }

  const clear = () => {
    onChange(null, null)
    setPickedName(null)
    setInput("")
  }

  return (
    <div className="fsp-admin__typeahead">
      {hasSelection ? (
        <div className="fsp-admin__typeahead-selected">
          <span>{displayName}</span>
          <button type="button" className="fsp-admin__typeahead-clear" onClick={clear} aria-label="Clear">×</button>
        </div>
      ) : (
        <input
          id={inputId}
          type="search"
          className="wb-clients__search-input"
          placeholder={placeholder}
          value={input}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 150)
          }}
          onChange={(e) => {
            setInput(e.target.value)
            setOpen(true)
          }}
        />
      )}

      {open && !hasSelection && (
        <ul
          className="fsp-admin__typeahead-menu"
          // Keep the press from moving focus at all. Clearing the blur timer
          // afterwards was a race: mousedown moves focus first, so a press held
          // longer than the 150ms close delay unmounted the menu before mouseup —
          // the click then landed on whatever sat underneath (in the assign modal,
          // the submit button), and the option was never chosen.
          onMouseDown={(e: React.MouseEvent) => {
            e.preventDefault()
            if (blurTimer.current) clearTimeout(blurTimer.current)
          }}
        >
          {isFetching && <li className="fsp-admin__typeahead-empty">Searching…</li>}
          {!isFetching && isError && <li className="fsp-admin__typeahead-empty">Search failed — try again</li>}
          {showOptions && options.length === 0 && <li className="fsp-admin__typeahead-empty">No matches</li>}
          {showOptions &&
            options.map((opt) => (
              <li key={opt.id}>
                <button type="button" className="fsp-admin__typeahead-option" onClick={() => select(opt)}>
                  {opt.name}
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  )
}
